import type { LucideIcon } from 'lucide-react';
import {
  Archive,
  ArrowUpDown,
  Bell,
  BellRing,
  Bot,
  Building2,
  Calculator,
  CalendarClock,
  CheckCircle2,
  Upload,
  Clock,
  Container,
  FileCheck2,
  FileDown,
  FileOutput,
  FileSignature,
  FileSpreadsheet,
  FileStack,
  FileText,
  Fingerprint,
  Gift,
  GitMerge,
  Globe,
  Hash,
  Leaf,
  ListChecks,
  MessageCircle,
  Navigation,
  Paperclip,
  Receipt,
  Route,
  Rows3,
  Scale,
  Send,
  ScanSearch,
  Shield,
  ShieldAlert,
  ShieldCheck,
  Smartphone,
  Star,
  TrendingUp,
  Truck,
  UserCog,
  Users,
  Wallet,
  Wand2,
  Wrench,
  Zap,
} from 'lucide-react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type FeatureStatus = 'shipped' | 'partial' | 'in_progress' | 'planned';

export interface Feature {
  icon: LucideIcon;
  titleKey: string;
  descKey: string;
  status: FeatureStatus;
}

export interface Release {
  version: string;
  dateKey: string;
  status: FeatureStatus;
  milestone?: boolean;
  features: Feature[];
}

// ---------------------------------------------------------------------------
// Data — update this when features ship. Keys live in i18n under `updates.*`.
//
// Versioning protocol (see CLAUDE.md "SPRINT PROTOCOL"): when a sprint ships
// tenant-visible functionality, add or extend a `shipped` Release entry here.
// If it's a standalone new release (not part of one already announced), add
// a new entry directly below CURRENT_VERSION's release with an incremented
// version (patch by default; minor if Stefan flags the sprint as a headline
// at approval time; major only on his explicit request) and point
// CURRENT_VERSION at it. The sidebar version badge and the dashboard "what's
// new" card both read CURRENT_VERSION automatically — nothing else to sync.
// ---------------------------------------------------------------------------

export const CURRENT_VERSION = 'v2.3.5';
const CURRENT_VERSION_DATE_KEY = 'updates.releases.v235.date';

function feature(icon: LucideIcon, key: string, status: FeatureStatus): Feature {
  return {
    icon,
    titleKey: `updates.features.${key}.title`,
    descKey: `updates.features.${key}.desc`,
    status,
  };
}

export const RELEASES: Release[] = [
  {
    version: 'Modul de contabilitate',
    dateKey: 'updates.releases.accountingModule.date',
    status: 'planned',
    milestone: true,
    features: [feature(Calculator, 'accountingModule', 'planned')],
  },
  {
    version: CURRENT_VERSION,
    dateKey: CURRENT_VERSION_DATE_KEY,
    status: 'shipped',
    features: [feature(Scale, 'bonCantarBorderou', 'shipped')],
  },
  {
    version: 'v2.3.4',
    dateKey: 'updates.releases.v234.date',
    status: 'shipped',
    features: [
      feature(Wrench, 'outgoingReopenStockFix', 'shipped'),
      feature(CheckCircle2, 'inlineConfirmationsOffScreen', 'shipped'),
      feature(Smartphone, 'mobileApkBarsFix', 'shipped'),
    ],
  },
  {
    version: 'v2.3.3',
    dateKey: 'updates.releases.v233.date',
    status: 'shipped',
    features: [
      feature(CheckCircle2, 'inlineConfirmationsEverywhere', 'shipped'),
      feature(Upload, 'fileDropImports', 'shipped'),
    ],
  },
  {
    version: 'v2.3.2',
    dateKey: 'updates.releases.v232.date',
    status: 'shipped',
    features: [feature(CheckCircle2, 'inlineConfirmationsIntakesInvoices', 'shipped')],
  },
  {
    version: 'v2.3.1',
    dateKey: 'updates.releases.v231.date',
    status: 'shipped',
    features: [
      feature(BellRing, 'apkPushPickups', 'shipped'),
      feature(Navigation, 'apkNativeLocation', 'shipped'),
      feature(Zap, 'wastesDialogsFaster', 'shipped'),
    ],
  },
  {
    version: 'v2.3.0',
    dateKey: 'updates.releases.v230.date',
    status: 'shipped',
    features: [
      feature(FileText, 'annexNoteAvizMention', 'shipped'),
      feature(Hash, 'annexNumberPerCode', 'shipped'),
      feature(Archive, 'wasteArchive', 'shipped'),
      feature(Wrench, 'wasteCatalogFixes', 'shipped'),
      feature(Fingerprint, 'rememberDeviceOnMfa', 'shipped'),
      feature(CheckCircle2, 'inlineConfirmations', 'partial'),
    ],
  },
  {
    version: 'v2.2.1',
    dateKey: 'updates.releases.v221.date',
    status: 'shipped',
    features: [
      feature(Send, 'isuNotice', 'shipped'),
      feature(FileText, 'formularInformareInBundle', 'shipped'),
    ],
  },
  {
    version: 'v2.2.0',
    dateKey: 'updates.releases.v220.date',
    status: 'shipped',
    features: [
      feature(Truck, 'outgoingTwoStep', 'shipped'),
      feature(Scale, 'stockDispatchedPending', 'shipped'),
    ],
  },
  {
    version: 'v2.1.4',
    dateKey: 'updates.releases.v214.date',
    status: 'shipped',
    features: [
      feature(Container, 'containerBoxes', 'shipped'),
      feature(Wrench, 'collectionDocumentsRefresh', 'shipped'),
    ],
  },
  {
    version: 'v2.1.3',
    dateKey: 'updates.releases.v213.date',
    status: 'shipped',
    features: [
      feature(Wrench, 'transporterFormCrashFix', 'shipped'),
      feature(Receipt, 'invoiceDraftEdit', 'shipped'),
      feature(FileText, 'invoiceLineWasteCode', 'shipped'),
    ],
  },
  {
    version: 'v2.1.2',
    dateKey: 'updates.releases.v212.date',
    status: 'shipped',
    features: [feature(Wrench, 'staleTabAutoReload', 'shipped')],
  },
  {
    version: 'v2.1.1',
    dateKey: 'updates.releases.v211.date',
    status: 'shipped',
    features: [
      feature(Scale, 'bonCantarPerCode', 'shipped'),
      feature(Users, 'receptionCommitteeSigner', 'shipped'),
      feature(ShieldAlert, 'annexOneHazardousOnly', 'shipped'),
    ],
  },
  {
    version: 'v2.1.0',
    dateKey: 'updates.releases.v210.date',
    status: 'shipped',
    features: [
      feature(Rows3, 'uiNewMenu', 'shipped'),
      feature(ScanSearch, 'uiGlobalSearch', 'shipped'),
      feature(ListChecks, 'uiFilterBar', 'shipped'),
      feature(ShieldAlert, 'uiClearErrors', 'shipped'),
      feature(Zap, 'uiReadableTables', 'shipped'),
    ],
  },
  {
    version: 'v2.0.2',
    dateKey: 'updates.releases.v202.date',
    status: 'shipped',
    features: [
      feature(FileText, 'avizTipizat', 'shipped'),
      feature(FileCheck2, 'anexaOutgoingParties', 'shipped'),
      feature(Leaf, 'fondMediuWithholding', 'shipped'),
      feature(Fingerprint, 'driverIdentityCard', 'shipped'),
      feature(Users, 'documentRepresentatives', 'shipped'),
      feature(Wallet, 'outgoingTariff', 'shipped'),
      feature(Hash, 'manualDocumentNumbers', 'shipped'),
      feature(ShieldCheck, 'annualVisa', 'shipped'),
      feature(FileSpreadsheet, 'securedRegisters', 'shipped'),
      feature(FileSignature, 'annualCertificates', 'shipped'),
      feature(Globe, 'siatdTab', 'shipped'),
      feature(Paperclip, 'outgoingPhotos', 'shipped'),
    ],
  },
  {
    version: 'v2.0.1',
    dateKey: 'updates.releases.v201.date',
    status: 'shipped',
    features: [
      feature(CalendarClock, 'pickupScheduleDays', 'shipped'),
      feature(Route, 'pickupZones', 'shipped'),
      feature(BellRing, 'pickupPortalRequests', 'shipped'),
      feature(Smartphone, 'mobileSheetAutosave', 'shipped'),
      feature(FileSignature, 'largerSignatures', 'shipped'),
    ],
  },
  {
    version: 'v2.0',
    dateKey: 'updates.releases.v20.date',
    status: 'shipped',
    features: [
      feature(Bot, 'vanaGreenAi', 'shipped'),
      feature(UserCog, 'hrModule', 'shipped'),
      feature(Route, 'pickupDispatch', 'shipped'),
      feature(FileSignature, 'eSignature', 'shipped'),
      feature(Navigation, 'fieldMobileApp', 'shipped'),
      feature(MessageCircle, 'autoNotifications', 'shipped'),
      feature(Receipt, 'eFacturaIntegration', 'shipped'),
      feature(Receipt, 'invoicing', 'shipped'),
      feature(Building2, 'partnerPortal', 'shipped'),
      feature(Scale, 'smartScales', 'shipped'),
      feature(Smartphone, 'mobileFieldAppPolish', 'shipped'),
      feature(GitMerge, 'wasteMerge', 'shipped'),
      feature(Send, 'pickupRemoteSigning', 'shipped'),
      feature(Fingerprint, 'sourceTraceability', 'shipped'),
      feature(Leaf, 'fondMediuInvoicing', 'shipped'),
      feature(FileStack, 'supplierSourceDocuments', 'shipped'),
      feature(Paperclip, 'collectionAttachments', 'shipped'),
    ],
  },
  {
    version: 'v1.2.6',
    dateKey: 'updates.releases.v126.date',
    status: 'shipped',
    features: [
      feature(TrendingUp, 'advancedAnalytics', 'shipped'),
      feature(Hash, 'externalNirNumbering', 'shipped'),
      feature(FileDown, 'bulkExport', 'shipped'),
      feature(FileText, 'companyContractTemplate', 'shipped'),
      feature(Building2, 'partnerPortalPolish', 'shipped'),
      feature(Scale, 'scaleConnectionHealth', 'shipped'),
      feature(Clock, 'hrClockInOut', 'shipped'),
    ],
  },
  {
    version: 'v1.2.5',
    dateKey: 'updates.releases.v125.date',
    status: 'shipped',
    features: [
      feature(Smartphone, 'signaturePad', 'shipped'),
      feature(ScanSearch, 'idScanner', 'shipped'),
      feature(Gift, 'referralDiscountAutomation', 'shipped'),
      feature(Wallet, 'simpleModePricing', 'shipped'),
      feature(Star, 'tariffDefaults', 'shipped'),
    ],
  },
  {
    version: 'v1.2.4',
    dateKey: 'updates.releases.v124.date',
    status: 'shipped',
    features: [
      feature(ScanSearch, 'lotTraceability', 'shipped'),
      feature(ArrowUpDown, 'treatmentCancelReopen', 'shipped'),
    ],
  },
  {
    version: 'v1.2.3',
    dateKey: 'updates.releases.v123.date',
    status: 'shipped',
    features: [
      feature(Bell, 'efacturaRejectionAlerts', 'shipped'),
      feature(CalendarClock, 'invoiceIssueDate', 'shipped'),
    ],
  },
  {
    version: 'v1.2.2',
    dateKey: 'updates.releases.v122.date',
    status: 'shipped',
    features: [
      feature(UserCog, 'managerRole', 'shipped'),
      feature(Wand2, 'simpleMode', 'shipped'),
      feature(ListChecks, 'anexaContentOverride', 'shipped'),
      feature(Hash, 'outgoingInternalNumber', 'shipped'),
    ],
  },
  {
    version: 'v1.2.1',
    dateKey: 'updates.releases.v121.date',
    status: 'shipped',
    features: [
      feature(Users, 'crmOffers', 'shipped'),
      feature(FileSignature, 'eSignatureStamp', 'shipped'),
      feature(Navigation, 'gpsAutoShutdown', 'shipped'),
      feature(TrendingUp, 'marketPricesInternational', 'partial'),
      feature(ArrowUpDown, 'sortToggle', 'shipped'),
      feature(Star, 'favoriteReports', 'shipped'),
      feature(Calculator, 'recursiveTariff', 'shipped'),
      feature(FileText, 'nirPricing', 'shipped'),
    ],
  },
  {
    version: 'v1.2.0',
    dateKey: 'updates.releases.v120.date',
    status: 'shipped',
    features: [
      feature(TrendingUp, 'marketPrices', 'shipped'),
      feature(Globe, 'whiteLabelDomain', 'shipped'),
      feature(Zap, 'publicApi', 'shipped'),
      feature(ShieldCheck, 'trustedDevices', 'shipped'),
      feature(Wallet, 'financialsModule', 'shipped'),
      feature(Bell, 'autoInvoicing', 'shipped'),
      feature(Receipt, 'tenantInvoicing', 'shipped'),
      feature(FileCheck2, 'importedCollectionsWorkflow', 'shipped'),
      feature(BellRing, 'notificationCenter', 'shipped'),
      feature(ShieldAlert, 'newDeviceLoginAlert', 'shipped'),
    ],
  },
  {
    version: 'v1.1.0',
    dateKey: 'updates.releases.v110.date',
    status: 'shipped',
    features: [
      feature(Route, 'dispatchBoard', 'shipped'),
      feature(Navigation, 'driverGpsTracking', 'shipped'),
      feature(MessageCircle, 'pickupWhatsappSms', 'shipped'),
      feature(Container, 'containerTracking', 'shipped'),
      feature(Wrench, 'fleetMaintenance', 'shipped'),
      feature(Smartphone, 'mobileContainers', 'shipped'),
      feature(Rows3, 'borderouCompactMode', 'shipped'),
    ],
  },
  {
    version: 'v1.0.0',
    dateKey: 'updates.releases.v100.date',
    status: 'shipped',
    features: [
      feature(Shield, 'gdprCompliance', 'shipped'),
      feature(FileSpreadsheet, 'auditLogUi', 'shipped'),
      feature(Truck, 'siatdExports', 'shipped'),
      feature(ScanSearch, 'anafVerification', 'shipped'),
      feature(Bot, 'siatdAutomation', 'shipped'),
      feature(ListChecks, 'exportReconciliation', 'shipped'),
      feature(FileOutput, 'ecoticModelExports', 'shipped'),
      feature(FileDown, 'sagaExport', 'shipped'),
    ],
  },
  {
    version: 'MVP 3',
    dateKey: 'updates.releases.mvp3.date',
    status: 'shipped',
    features: [
      feature(CheckCircle2, 'mobileApp', 'shipped'),
      feature(CheckCircle2, 'pickupSchedule', 'shipped'),
      feature(CheckCircle2, 'duplicateCollection', 'shipped'),
      feature(CheckCircle2, 'companyNotes', 'shipped'),
      feature(CheckCircle2, 'tenantLogo', 'shipped'),
      feature(CheckCircle2, 'documentTemplates', 'shipped'),
    ],
  },
  {
    version: 'MVP 2',
    dateKey: 'updates.releases.mvp2.date',
    status: 'shipped',
    features: [
      feature(CheckCircle2, 'accountingExport', 'shipped'),
      feature(CheckCircle2, 'notifications', 'shipped'),
      feature(CheckCircle2, 'anpmReports', 'shipped'),
      feature(CheckCircle2, 'legalReports', 'shipped'),
    ],
  },
  {
    version: 'MVP 1',
    dateKey: 'updates.releases.mvp1.date',
    status: 'shipped',
    features: [
      feature(CheckCircle2, 'auth', 'shipped'),
      feature(CheckCircle2, 'catalog', 'shipped'),
      feature(CheckCircle2, 'collections', 'shipped'),
      feature(CheckCircle2, 'stock', 'shipped'),
      feature(CheckCircle2, 'outgoing', 'shipped'),
      feature(CheckCircle2, 'treatment', 'shipped'),
      feature(CheckCircle2, 'documents', 'shipped'),
      feature(CheckCircle2, 'reports', 'shipped'),
      feature(CheckCircle2, 'settings', 'shipped'),
      feature(CheckCircle2, 'support', 'shipped'),
    ],
  },
];

export function getCurrentRelease(): Release | undefined {
  return RELEASES.find((r) => r.version === CURRENT_VERSION);
}
